import { useState, useMemo } from "react"
import BuyerCampaignTemplateForm from "./BuyerCampaignTemplateForm"
import {
  useCreateBuyerCampaignTemplateMutation,
  useUpdateBuyerCampaignTemplateMutation,
  useDeleteBuyerCampaignTemplateMutation,
} from "apis/buyerCampaign"
import Button from "components/ui/Button"
import { FiEdit2, FiTrash2 } from "react-icons/fi"
import Modal from "components/ui/Modal"
import { FaPlus } from "react-icons/fa6"
import Table from "components/ui/Table"

const TRIGGER_LABELS = {
  closingDate: "Closing Date",
  auctionDate: "Auction Date",
  firstOpenHome: "First Open Home",
  anyOpenHome: "Any Open Home",
  campaignCreated: "Campaign Created",
  sold: "Sold",
  underContract: "Under Contract",
  auctionPassedIn: "Auction Passed In",
  closingViaPT: "Closing Via PT",
  priceGuideUpdated: "Price Guide Updated",
  goLive: "Go Live",
}

const ACTION_LABELS = {
  before: "Before",
  after: "After",
  exact: "Exact",
  immediate: "Immediate",
}

const BuyerCampaignTemplates = ({ templates }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [formMode, setFormMode] = useState("create")
  const pageSize = 10

  const {
    mutate: createTemplate,
    isPending: isCreating,
  } = useCreateBuyerCampaignTemplateMutation()

  const {
    mutate: updateTemplate,
    isPending: isUpdating,
  } = useUpdateBuyerCampaignTemplateMutation()

  const {
    mutate: deleteTemplate,
    isPending: isDeleting,
  } = useDeleteBuyerCampaignTemplateMutation()

  const handleCreate = () => {
    setSelectedTemplate(null)
    setFormMode("create")
    setIsFormOpen(true)
  }

  const handleEdit = (template) => {
    setSelectedTemplate(template)
    setFormMode("edit")
    setIsFormOpen(true)
  }

  const handleDeleteClick = (template) => {
    setSelectedTemplate(template)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    deleteTemplate(selectedTemplate._id)
    setIsDeleteModalOpen(false)
    setSelectedTemplate(null)
  }

  const handleFormSubmit = (templateData) => {
    if (formMode === "create") {
      createTemplate(templateData)
    } else {
      updateTemplate({ id: selectedTemplate._id, ...templateData })
    }
    setIsFormOpen(false)
    setSelectedTemplate(null)
  }

  const handleRowClick = (template) => {
    setSelectedTemplate(template)
    setIsDetailOpen(true)
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
      },
      {
        accessorKey: "trigger",
        header: "Trigger",
        cell: (row) => TRIGGER_LABELS[row.trigger] || row.trigger,
        isFilter: true,
      },
      {
        accessorKey: "action",
        header: "Action",
        cell: (row) => {
          const label = ACTION_LABELS[row.action] || row.action
          const offset = row.offsetDays ? ` (${row.offsetDays}d)` : ""
          return `${label}${offset}`
        },
        isFilter: true,
      },
      {
        accessorKey: "saleProcess",
        header: "Sale Process",
        cell: (row) =>
          row.saleProcess
            ?.map((s) => (s === "privateTreaty" ? "PT" : "Auction"))
            .join(", ") || "-",
        isFilter: true,
      },
      {
        accessorKey: "platform",
        header: "Platform",
        cell: (row) => row.platform?.join(", ") || "-",
        isFilter: false,
      },
      {
        accessorKey: "isActive",
        header: "Status",
        cell: (row) => (
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              row.isActive
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {row.isActive ? "Active" : "Inactive"}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleEdit(row)
              }}
              className="edit-icon"
              title="Edit Template"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteClick(row)
              }}
              className="delete-icon"
              title="Delete Template"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    []
  )

  const CreateButton = () => (
    <Button onClick={handleCreate} className="black-button flex items-center">
      <FaPlus size={14} className="mr-2" />
      New Template
    </Button>
  )

  return (
    <div>
      {/* Create / Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setSelectedTemplate(null)
        }}
        className="max-w-3xl"
        title={formMode === "create" ? "New Buyer Campaign Template" : "Edit Buyer Campaign Template"}
      >
        <BuyerCampaignTemplateForm
          template={selectedTemplate}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsFormOpen(false)
            setSelectedTemplate(null)
          }}
          formMode={formMode}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setSelectedTemplate(null)
        }}
        title="Delete Template"
      >
        <p className="text-sm text-darkgray mb-4">
          Are you sure you want to delete "{selectedTemplate?.name}"? This action
          cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <Button
            onClick={() => {
              setIsDeleteModalOpen(false)
              setSelectedTemplate(null)
            }}
            className="outline-button"
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} className="black-button">
            Delete
          </Button>
        </div>
      </Modal>

      {/* Detail View */}
      <Modal
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false)
          setSelectedTemplate(null)
        }}
        className="max-w-3xl"
        title="Template Details"
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-darkgray">Name</span>
                <p className="text-black">{selectedTemplate.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Status</span>
                <p>
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      selectedTemplate.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {selectedTemplate.isActive ? "Active" : "Inactive"}
                  </span>
                </p>
              </div>
            </div>

            {selectedTemplate.description && (
              <div>
                <span className="text-sm font-medium text-darkgray">Description</span>
                <p className="text-black">{selectedTemplate.description}</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm font-medium text-darkgray">Action</span>
                <p className="text-black">{ACTION_LABELS[selectedTemplate.action]}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Trigger</span>
                <p className="text-black">{TRIGGER_LABELS[selectedTemplate.trigger]}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Offset Days</span>
                <p className="text-black">{selectedTemplate.offsetDays}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm font-medium text-darkgray">Weekday</span>
                <p className="text-black capitalize">{selectedTemplate.weekday || "None"}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Time</span>
                <p className="text-black">{selectedTemplate.time || "Exact trigger time"}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Scenario</span>
                <p className="text-black capitalize">{selectedTemplate.scenario}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Week on market</span>
                <p className="text-black">{selectedTemplate.weekOnMarket ?? "None"}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Minimum inspections</span>
                <p className="text-black">{selectedTemplate.minInspections ?? 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-darkgray">Sale Process</span>
                <div className="flex gap-1 mt-1">
                  {selectedTemplate.saleProcess?.map((s) => (
                    <span
                      key={s}
                      className="px-2 py-0.5 text-xs rounded-full bg-lightgray text-darkergray"
                    >
                      {s === "privateTreaty" ? "Private Treaty" : "Auction"}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-darkgray">Platform</span>
                <div className="flex gap-1 mt-1">
                  {selectedTemplate.platform?.map((p) => (
                    <span
                      key={p}
                      className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 capitalize"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <span className="px-2 py-0.5 text-xs rounded-full bg-lightgray text-darkgray">
                Auto-send when buyer updates are on
              </span>
              {selectedTemplate.isPostDeadline && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-800">
                  Post Deadline
                </span>
              )}
            </div>

            {selectedTemplate.content && (
              <div>
                <span className="text-sm font-medium text-darkgray">Content</span>
                <div className="mt-1 p-3 bg-lightergray rounded text-sm text-darkergray whitespace-pre-wrap">
                  {selectedTemplate.content}
                </div>
              </div>
            )}

            {selectedTemplate.prompt && (
              <div>
                <span className="text-sm font-medium text-darkgray">AI Prompt</span>
                <div className="mt-1 p-3 bg-lightergray rounded text-sm text-darkergray whitespace-pre-wrap">
                  {selectedTemplate.prompt}
                </div>
              </div>
            )}

            {selectedTemplate.specialInstructions && (
              <div>
                <span className="text-sm font-medium text-darkgray">Special Instructions</span>
                <div className="mt-1 p-3 bg-lightergray rounded text-sm text-darkergray whitespace-pre-wrap">
                  {selectedTemplate.specialInstructions}
                </div>
              </div>
            )}

            {selectedTemplate.objections && selectedTemplate.objections.length > 0 && (
              <div>
                <span className="text-sm font-medium text-darkgray">Objections</span>
                <div className="mt-1 space-y-3">
                  {selectedTemplate.objections.map((o, idx) => (
                    <div key={idx} className="p-3 bg-lightergray rounded text-sm text-darkergray">
                      {o.objection && (
                        <div>
                          <span className="text-sm font-medium text-darkgray">Objection</span>
                          <p className="text-black">{o.objection}</p>
                        </div>
                      )}

                      {o.preempt && (
                        <div className="mt-1">
                          <span className="text-sm font-medium text-darkgray">Preempt</span>
                          <p className="text-black">{o.preempt}</p>
                        </div>
                      )}

                      {o.howItLands && (
                        <div className="mt-1">
                          <span className="text-sm font-medium text-darkgray">How It Lands</span>
                          <p className="text-black">{o.howItLands}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                onClick={() => {
                  setIsDetailOpen(false)
                  handleEdit(selectedTemplate)
                }}
                className="black-button"
              >
                Edit Template
              </Button>
              <Button
                onClick={() => {
                  setIsDetailOpen(false)
                  setSelectedTemplate(null)
                }}
                className="outline-button"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4 mt-4">
       <div className="ml-3">
            <h3 className="text-sm font-semibold text-yellow-800">Disclaimer</h3>
            <p className="text-sm text-yellow-700 mt-1">
              These templates are the <strong>master definitions</strong> used to generate buyer campaign messages. Any changes you make here will be applied to connected property campaigns <strong>at the time of sending</strong> — they will not retroactively update messages that have already been sent or are currently scheduled.
            </p>
          </div>
      </div>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title=""
          columns={columns}
          data={templates || []}
          pageSize={pageSize}
          ButtonCmp={CreateButton}
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  )
}

export default BuyerCampaignTemplates
